import React, { useState } from 'react';
import ESATileDashboard from './ESATileDashboard';
import logger from '../utils/logger';

const ESAReportPurchase = ({ assessmentId, onPurchaseComplete }) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [isProcessing, setIsProcessing] = useState(false);
  const [purchaseResult, setPurchaseResult] = useState(null);
  const [error, setError] = useState(null);
  const [reportType, setReportType] = useState('clean'); // Default to clean (new revolutionary design)
  const [showTileDashboard, setShowTileDashboard] = useState(false);
  const [reportData, setReportData] = useState(null);
  
  // Form state for purchase information
  const [formData, setFormData] = useState({
    industry: 'Food & Beverage',
    company_name: '',
    facility_size: 'Medium (50,000-200,000 sq ft)',
    annual_energy_spend: 250000,
    contact_email: '',
    contact_name: '',
    payment_method: 'card'
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePurchase = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    setError(null);

    try {
      const purchaseData = {
        user_id: `user_${Date.now()}`,
        assessment_id: assessmentId || `assessment_${Date.now()}`,
        industry: formData.industry,
        company_name: formData.company_name,
        facility_size: formData.facility_size,
        annual_energy_spend: parseFloat(formData.annual_energy_spend),
        contact_email: formData.contact_email,
        contact_name: formData.contact_name,
        assessment_responses: { sample: 'data' }, // TODO: Get from actual assessment
        payment_method: formData.payment_method,
        amount: 990.0
      };

      // Choose endpoint based on report type
      const endpoint = reportType === 'clean'
        ? `${backendUrl}/api/reports/clean/purchase`
        : reportType === 'tiles' 
        ? `${backendUrl}/api/reports/tiles/purchase`
        : reportType === 'executive'
        ? `${backendUrl}/api/reports/executive/purchase`
        : `${backendUrl}/api/reports/purchase`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(purchaseData)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setPurchaseResult(result);
        
        // Show tile dashboard for purchased report
        const purchaseReportData = {
          company_name: formData.company_name,
          industry: formData.industry,
          facility_size: formData.facility_size,
          total_annual_savings: result.total_annual_savings || 1172804,
          opportunities_count: result.opportunities_count || 15,
          roi_percentage: result.roi_percentage || 118465,
          report_id: result.report_id,
          download_url: result.download_url,
          transaction_id: result.transaction_id
        };
        
        setReportData(purchaseReportData);
        setShowTileDashboard(true);
        
        if (onPurchaseComplete) {
          onPurchaseComplete(result);
        }
      } else {
        setError(result.detail || 'Purchase failed');
      }
    } catch (err) {
      setError('Network error occurred');
      logger.error('Purchase error:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSampleReport = async () => {
    setIsProcessing(true);
    setError(null);

    try {
      logger.log('🎯 Starting tile-based sample generation...');
      
      // Choose sample endpoint based on report type
      const endpoint = reportType === 'clean'
        ? `${backendUrl}/api/reports/clean/generate-sample`
        : reportType === 'tiles'
        ? `${backendUrl}/api/reports/tiles/generate-sample`
        : reportType === 'executive'
        ? `${backendUrl}/api/reports/executive/generate-sample`
        : `${backendUrl}/api/reports/generate-sample`;

      logger.log('📡 Calling endpoint:', endpoint);

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      logger.log('📥 Response status:', response.status);
      
      const result = await response.json();
      logger.log('📊 API Result:', result);

      if (response.ok && result.success) {
        // Pass ALL comprehensive data from API to tile dashboard
        const sampleReportData = {
          // Basic company info
          company_name: result.company_name || formData.company_name || 'Demo Manufacturing Co.',
          industry: result.industry || formData.industry,
          facility_size: result.facility_size || formData.facility_size,
          analysis_date: result.analysis_date,
          
          // Financial metrics
          total_annual_savings: result.total_annual_savings,
          opportunities_count: result.opportunities_count,
          roi_percentage: result.roi_percentage,
          
          // Energy metrics
          energy_saved_kwh: result.energy_saved_kwh,
          peak_demand_kw: result.peak_demand_kw,
          payback_years: result.payback_years,
          
          // Portfolio metrics
          portfolio_roi: result.portfolio_roi,
          three_year_value: result.three_year_value,
          five_year_value: result.five_year_value,
          efficiency_score: result.efficiency_score,
          
          // Opportunities data
          priority_opportunities: result.priority_opportunities || [],
          
          // Report metadata
          report_id: result.report_id,
          download_url: result.download_url
        };
        
        logger.log('🎨 Setting report data for tiles:', sampleReportData);
        
        setReportData(sampleReportData);
        setShowTileDashboard(true);
        
        logger.log('✅ Tile dashboard should now be visible');
      } else {
        logger.error('❌ API Error:', result);
        setError(result.detail || 'Sample generation failed');
      }
    } catch (err) {
      logger.error('❌ Network Error:', err);
      setError('Network error occurred');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBasicSampleReport = async () => {
    setIsProcessing(true);
    setError(null);

    try {
      logger.log('📄 Starting basic sample PDF generation...');
      
      const endpoint = `${backendUrl}/api/reports/basic-sample`;
      logger.log('📡 Calling endpoint:', endpoint);

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      logger.log('📥 Response status:', response.status);
      
      const result = await response.json();
      logger.log('📊 API Result:', result);

      if (response.ok && result.success) {
        // For basic PDF, directly download the file
        if (result.download_url) {
          window.open(`${backendUrl}${result.download_url}`, '_blank');
        } else {
          setError('PDF download URL not available');
        }
      } else {
        logger.error('❌ API Error:', result);
        setError(result.detail || 'Basic sample generation failed');
      }
    } catch (err) {
      logger.error('❌ Network Error:', err);
      setError('Network error occurred');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleExportPDF = async () => {
    if (reportData && reportData.download_url) {
      // Open PDF download
      window.open(`${backendUrl}${reportData.download_url}`, '_blank');
    } else {
      setError('No report available for PDF export');
    }
  };

  // Show Tile Dashboard if requested
  if (showTileDashboard && reportData) {
    return (
      <div>
        <div className="mb-4 p-4 bg-blue-50 rounded-lg flex items-center justify-between">
          <div>
            <h3 className="font-bold text-blue-800">Interactive Tile Dashboard Generated!</h3>
            <p className="text-blue-600">Review your report below, then export to PDF when ready.</p>
          </div>
          <button
            onClick={() => setShowTileDashboard(false)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            ← Back to Form
          </button>
        </div>
        <ESATileDashboard 
          reportData={reportData} 
          onExportPDF={handleExportPDF}
        />
      </div>
    );
  }

  if (purchaseResult) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <div className="flex items-center mb-4">
          <div className="flex-shrink-0">
            <svg className="h-8 w-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-lg font-medium text-green-800">Purchase Successful!</h3>
            <p className="text-green-700">Your ESA Opportunity Report has been generated.</p>
          </div>
        </div>
        
        <div className="bg-white rounded-lg p-4 mb-4">
          <h4 className="font-semibold text-gray-900 mb-2">Report Details:</h4>
          <ul className="space-y-1 text-sm text-gray-600">
            <li><strong>Report ID:</strong> {purchaseResult.report_id}</li>
            <li><strong>Transaction ID:</strong> {purchaseResult.transaction_id}</li>
            <li><strong>Annual Savings Identified:</strong> ${purchaseResult.total_annual_savings?.toLocaleString()}</li>
            <li><strong>Opportunities Found:</strong> {purchaseResult.opportunities_count}</li>
          </ul>
        </div>

        <div className="flex space-x-4">
          {purchaseResult.download_url && (
            <a
              href={`${backendUrl}${purchaseResult.download_url}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors"
            >
              Download Report PDF
            </a>
          )}
          <button
            onClick={() => window.location.href = '/leap-program'}
            className="border border-emerald-600 text-emerald-600 px-4 py-2 rounded-lg hover:bg-emerald-50 transition-colors"
          >
            Explore LEAP Program
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-6">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">ESA Opportunity Report</h2>
          <p className="text-gray-600">Executive-grade energy productivity analysis and strategic roadmap</p>
        </div>

        {/* Report Type Selection */}
        <div className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-lg p-4 mb-6 border border-emerald-200">
          <div className="flex items-center mb-3">
            <span className="text-2xl mr-2">🎨</span>
            <h3 className="font-bold text-emerald-800">NEW: Tile-Based Redesigned Report!</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-emerald-700 mb-4">
            <div>
              <div className="font-medium mb-2">🔥 Visual-First Design:</div>
              • Modular tile layout<br/>
              • Executive-friendly scanning<br/>
              • Clear visual hierarchy<br/>
              • Rounded corners & shadows
            </div>
            <div>
              <div className="font-medium mb-2">📊 Enhanced Sections:</div>
              • Priority Opportunities tiles<br/>
              • Financial Analysis charts<br/>
              • Interactive KPI dashboard<br/>
              • Implementation roadmap
            </div>
            <div>
              <div className="font-medium mb-2">💼 Executive Features:</div>
              • Board-ready presentation<br/>
              • LEAP program integration<br/>
              • Benchmarking comparisons<br/>
              • Professional branding
            </div>
          </div>
          
          <div className="bg-white p-3 rounded border border-emerald-300">
            <p className="text-emerald-800 text-sm font-medium">
              🚀 <strong>Revolutionary Design:</strong> Moving from dense consultant tables to modern, 
              scannable tile format. Each section is a self-contained visual module optimized for 
              executive decision-making.
            </p>
          </div>
        </div>

        <form onSubmit={handlePurchase} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company Name *
              </label>
              <input
                type="text"
                name="company_name"
                value={formData.company_name}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="Your Company Name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Industry Sector
              </label>
              <select
                name="industry"
                value={formData.industry}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="Food & Beverage">Food & Beverage</option>
                <option value="Metal Products">Metal Products</option>
                <option value="Chemicals & Pharmaceuticals">Chemicals & Pharmaceuticals</option>
                <option value="Plastics & Packaging">Plastics & Packaging</option>
                <option value="Mining & Primary Metals">Mining & Primary Metals</option>
                <option value="Facilities Management">Facilities Management</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Name *
              </label>
              <input
                type="text"
                name="contact_name"
                value={formData.contact_name}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="Executive Decision Maker"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address *
              </label>
              <input
                type="email"
                name="contact_email"
                value={formData.contact_email}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="executive@company.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Facility Classification
              </label>
              <select
                name="facility_size"
                value={formData.facility_size}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="Small (<50,000 sq ft)">Small (&lt;50,000 sq ft)</option>
                <option value="Medium (50,000-200,000 sq ft)">Medium (50,000-200,000 sq ft)</option>
                <option value="Large (200,000+ sq ft)">Large (200,000+ sq ft)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Annual Energy Investment ($)
              </label>
              <input
                type="number"
                name="annual_energy_spend"
                value={formData.annual_energy_spend}
                onChange={handleInputChange}
                min="10000"
                step="1000"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="350000"
              />
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          <div className="border-t pt-6">
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-lg p-6 text-white mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold">Tile-Based Executive Report: $990</h3>
                  <p className="text-emerald-100">Revolutionary modular design with visual-first tile layout</p>
                  <p className="text-emerald-100 font-medium mt-1">🎨 Scannable • 📊 Visual • 💼 Executive-Ready</p>
                </div>
                <div className="text-right">
                  <div className="text-emerald-100 text-sm">Expected ROI</div>
                  <div className="text-3xl font-bold">118,000%+</div>
                </div>
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                type="submit"
                disabled={isProcessing}
                className="flex-1 bg-emerald-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isProcessing ? 'Processing...' : 'Purchase Tile-Based Report - $990'}
              </button>
            </div>
            
            <div className="flex gap-4 justify-center">
              <button
                type="button"
                onClick={handleSampleReport}
                disabled={isProcessing}
                className="border border-emerald-600 text-emerald-600 px-6 py-3 rounded-lg font-medium hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isProcessing ? 'Generating...' : 'Tile-Based Sample (Free)'}
              </button>
              
              <button
                type="button"
                onClick={handleBasicSampleReport}
                disabled={isProcessing}
                className="border border-blue-600 text-blue-600 px-6 py-3 rounded-lg font-medium hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isProcessing ? 'Generating...' : 'Basic Sample (PDF)'}
              </button>
            </div>
            
            <div className="mt-4 text-center">
              <p className="text-xs text-gray-500">
                🎨 Tile-based design • 📊 Visual-first layout • 💼 Executive optimized • 🚀 Revolutionary format
              </p>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ESAReportPurchase;